import { Avatar, Chip, useTheme } from '@mui/material';
import type { ChipProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';

type StatusVariant =
  | 'todo'
  | 'done'
  | 'completed'
  | 'expired'
  | 'pendingDelegation'
  | 'delegated'
  | 'delegationRequest'
  | 'promotionWindow';

interface StatusConfig {
  label: string;
  // When chipColor is set, MUI's color tokens drive bg/text. When set to
  // null, we instead use bgColorToken / textColorToken to read theme paths.
  chipColor: ChipProps['color'] | null;
  bgColorToken: string | null;
  textColorToken: string | null;
  iconBgColor: string | null;
  icon: React.ReactNode | null;
}

const STATUS_CONFIG: Record<StatusVariant, StatusConfig> = {
  todo: {
    label: 'TO DO',
    chipColor: 'info',
    bgColorToken: null,
    textColorToken: null,
    iconBgColor: null,
    icon: null,
  },
  done: {
    label: 'DONE',
    chipColor: 'success',
    bgColorToken: null,
    textColorToken: null,
    iconBgColor: null,
    icon: null,
  },
  completed: {
    label: 'COMPLETED',
    chipColor: 'success',
    bgColorToken: null,
    textColorToken: null,
    iconBgColor: null,
    icon: null,
  },
  expired: {
    label: 'EXPIRED',
    chipColor: 'default',
    bgColorToken: null,
    textColorToken: null,
    iconBgColor: null,
    icon: null,
  },
  pendingDelegation: {
    label: 'PENDING DELEGATION',
    chipColor: 'warning',
    bgColorToken: null,
    textColorToken: null,
    iconBgColor: null,
    icon: null,
  },
  delegated: {
    label: 'DELEGATED',
    chipColor: 'secondary',
    bgColorToken: null,
    textColorToken: null,
    iconBgColor: null,
    icon: null,
  },
  delegationRequest: {
    label: 'DELEGATION REQUEST',
    chipColor: 'warning',
    bgColorToken: null,
    textColorToken: null,
    iconBgColor: null,
    icon: null,
  },
  promotionWindow: {
    label: 'PROMOTION WINDOW',
    chipColor: null,
    bgColorToken: 'custom.brightTeal',
    textColorToken: 'common.white',
    iconBgColor: null,
    icon: null,
  },
};

const DONE_STATUSES = ['Done', 'Finalised', 'Delivered'];

function getVariant(status: string): StatusVariant {
  if (status in STATUS_CONFIG) {
    return status as StatusVariant;
  }
  if (status === 'Completed') {
    return 'completed';
  }
  if (status === 'Expired') {
    return 'expired';
  }
  return DONE_STATUSES.includes(status) ? 'done' : 'todo';
}

interface StatusChipProps {
  status: string;
}

/**
 * Resolve a dot-path like `"custom.gold"` against the MUI theme's palette tree.
 * Returns the resolved string (e.g., a hex color) or `undefined` if any
 * segment is missing — which lets the `Chip`'s sx prop fall through to the
 * default and keeps the runtime safe if the theme is restructured.
 */
function resolvePaletteToken(theme: Theme, token: string): string | undefined {
  const segments = token.split('.');
  let current: unknown = theme.palette;
  for (const segment of segments) {
    if (current && typeof current === 'object' && segment in (current as Record<string, unknown>)) {
      current = (current as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }
  return typeof current === 'string' ? current : undefined;
}

export function StatusChip({ status }: StatusChipProps) {
  const theme = useTheme();
  const variant = getVariant(status);
  const config = STATUS_CONFIG[variant];

  // Variants that use custom theme paths bypass the MUI `color` prop and
  // drive bg/text via `sx` so we can pull palette tokens like `custom.gold`.
  const usesCustomTokens = config.chipColor === null && config.bgColorToken !== null;
  const customSx = usesCustomTokens
    ? {
        bgcolor: resolvePaletteToken(theme, config.bgColorToken!) ?? undefined,
        color: config.textColorToken
          ? resolvePaletteToken(theme, config.textColorToken)
          : undefined,
      }
    : undefined;

  return (
    <Chip
      label={config.label}
      size="medium"
      color={config.chipColor ?? 'default'}
      variant="filled"
      sx={customSx}
      icon={
        config.icon ? (
          <Avatar
            variant="circular"
            sx={{
              bgcolor: config.iconBgColor,
              width: 24,
              height: 24,
            }}
          >
            {config.icon}
          </Avatar>
        ) : undefined
      }
    />
  );
}

export default StatusChip;
